import { AdjustCrop, AdjustDocument, AdjustSticker, StickerFont, StickerIconId } from './adjust.types'
import { ComponentType, createElement } from 'react'
import { InterVariableFontFamily, RhetorikFontFamily } from 'constants/styling/theme'

import BalconyOutlinedIcon from '@mui/icons-material/BalconyOutlined'
import BathtubOutlinedIcon from '@mui/icons-material/BathtubOutlined'
import BedOutlinedIcon from '@mui/icons-material/BedOutlined'
import FireplaceOutlinedIcon from '@mui/icons-material/FireplaceOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import KitchenOutlinedIcon from '@mui/icons-material/KitchenOutlined'
import LocalParkingOutlinedIcon from '@mui/icons-material/LocalParkingOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import ParkOutlinedIcon from '@mui/icons-material/ParkOutlined'
import { Point, totalRotation } from './adjustGeometry'
import PoolOutlinedIcon from '@mui/icons-material/PoolOutlined'
import SellOutlinedIcon from '@mui/icons-material/SellOutlined'
import SquareFootOutlinedIcon from '@mui/icons-material/SquareFootOutlined'
import StairsOutlinedIcon from '@mui/icons-material/StairsOutlined'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined'
import { renderToStaticMarkup } from 'react-dom/server'

export const STICKER_ICON_COMPONENTS: Record<StickerIconId, ComponentType> = {
  home: HomeOutlinedIcon,
  bed: BedOutlinedIcon,
  bathtub: BathtubOutlinedIcon,
  square_foot: SquareFootOutlinedIcon,
  key: KeyOutlinedIcon,
  sell: SellOutlinedIcon,
  location: LocationOnOutlinedIcon,
  parking: LocalParkingOutlinedIcon,
  pool: PoolOutlinedIcon,
  sun: WbSunnyOutlinedIcon,
  park: ParkOutlinedIcon,
  kitchen: KitchenOutlinedIcon,
  balcony: BalconyOutlinedIcon,
  fireplace: FireplaceOutlinedIcon,
  stairs: StairsOutlinedIcon,
  star: StarOutlineIcon,
}

const ICON_RASTER_SIZE = 512

/** An MUI icon as a standalone SVG: the icon's fill comes from MUI's stylesheet, so it is written on the root here */
export const iconSvgMarkup = (icon: StickerIconId, color: string): string => {
  const markup = renderToStaticMarkup(createElement(STICKER_ICON_COMPONENTS[icon]))
  return markup.replace('<svg ', `<svg xmlns="http://www.w3.org/2000/svg" width="${ICON_RASTER_SIZE}" height="${ICON_RASTER_SIZE}" fill="${color}" `)
}

export const STICKER_FONT_FAMILY: Record<StickerFont, string> = {
  sans: InterVariableFontFamily,
  brand: RhetorikFontFamily,
}

const STICKER_FONT_WEIGHT: Record<StickerFont, number> = { sans: 600, brand: 400 }

/** Text sticker boxes are 1.3 × the font size high */
export const TEXT_LINE_RATIO = 1.3

export const stickerFontCss = (font: StickerFont, fontSize: number) => `${STICKER_FONT_WEIGHT[font]} ${fontSize}px ${STICKER_FONT_FAMILY[font]}`

let measureContext: CanvasRenderingContext2D | null | undefined

/** Width of a text sticker of the given height (an estimate where canvas is unavailable, e.g. jsdom) */
export const measureStickerText = (text: string, font: StickerFont, height: number): number => {
  if (measureContext === undefined) measureContext = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null
  const fontSize = height / TEXT_LINE_RATIO
  const padding = fontSize * 0.4
  if (!measureContext) return text.length * fontSize * 0.6 + padding
  measureContext.font = stickerFontCss(font, fontSize)
  return measureContext.measureText(text).width + padding
}

/**
 * Images the stickers need: icons (rasterised per colour), uploaded logos, and the text fonts. `onChange` fires when
 * something finishes loading, so the canvas can redraw.
 */
export class StickerAssets {
  private readonly icons = new Map<string, HTMLImageElement>()
  private readonly loading = new Map<string, Promise<void>>()
  private readonly logos = new Map<string, CanvasImageSource & { width: number, height: number }>()
  private readonly onChange: () => void

  constructor(onChange: () => void) {
    this.onChange = onChange
  }

  private loadIcon(icon: StickerIconId, color: string): Promise<void> {
    const key = `${icon}|${color}`
    const pending = this.loading.get(key)
    if (pending) return pending
    const promise = new Promise<void>((resolve) => {
      const image = new Image()
      image.onload = () => {
        this.icons.set(key, image)
        this.onChange()
        resolve()
      }
      image.onerror = () => resolve()
      image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(iconSvgMarkup(icon, color))}`
    })
    this.loading.set(key, promise)
    return promise
  }

  getIcon(icon: StickerIconId, color: string): HTMLImageElement | null {
    const image = this.icons.get(`${icon}|${color}`)
    if (!image) this.loadIcon(icon, color)
    return image ?? null
  }

  registerLogo(id: string, image: CanvasImageSource & { width: number, height: number }): void {
    this.logos.set(id, image)
    this.onChange()
  }

  getLogo(id: string) {
    return this.logos.get(id) ?? null
  }

  /** Everything the document's stickers need, loaded (the save waits for this) */
  async ensure(doc: AdjustDocument): Promise<void> {
    const waits: Promise<unknown>[] = []
    for (const sticker of doc.stickers) {
      if (sticker.kind === 'icon' && sticker.icon) waits.push(this.loadIcon(sticker.icon, sticker.color))
      if (sticker.kind === 'text' && sticker.font && typeof document !== 'undefined' && document.fonts?.load) {
        waits.push(document.fonts.load(stickerFontCss(sticker.font, 48)).catch(() => undefined))
      }
    }
    await Promise.all(waits)
  }
}

/** Draws one sticker in IMAGE coordinates (the caller sets the transform) */
export const drawAdjustSticker = (ctx: CanvasRenderingContext2D, sticker: AdjustSticker, assets: StickerAssets): void => {
  ctx.save()
  ctx.translate(sticker.x, sticker.y)
  ctx.rotate(sticker.rotation * Math.PI / 180)
  if (sticker.flipX) ctx.scale(-1, 1)
  ctx.globalAlpha = sticker.opacity
  if (sticker.kind === 'icon' && sticker.icon) {
    const image = assets.getIcon(sticker.icon, sticker.color)
    if (image) ctx.drawImage(image, -sticker.width / 2, -sticker.height / 2, sticker.width, sticker.height)
  } else if (sticker.kind === 'text' && sticker.text) {
    ctx.font = stickerFontCss(sticker.font ?? 'sans', sticker.height / TEXT_LINE_RATIO)
    ctx.fillStyle = sticker.color
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(sticker.text, 0, 0, sticker.width)
  } else if (sticker.kind === 'logo' && sticker.logoId) {
    const image = assets.getLogo(sticker.logoId)
    if (image) ctx.drawImage(image, -sticker.width / 2, -sticker.height / 2, sticker.width, sticker.height)
  }
  ctx.restore()
}

const rotate = (x: number, y: number, degrees: number): Point => {
  const r = degrees * Math.PI / 180
  return { x: x * Math.cos(r) - y * Math.sin(r), y: x * Math.sin(r) + y * Math.cos(r) }
}

/** Corners in IMAGE coordinates: top-left, top-right, bottom-right, bottom-left */
export const stickerCorners = (sticker: AdjustSticker): Point[] => [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([sx, sy]) => {
  const p = rotate(sx * sticker.width / 2, sy * sticker.height / 2, sticker.rotation)
  return { x: sticker.x + p.x, y: sticker.y + p.y }
})

/** Topmost sticker under an IMAGE-space point */
export const hitSticker = (stickers: AdjustSticker[], p: Point): AdjustSticker | null => {
  for (let i = stickers.length - 1; i >= 0; i--) {
    const sticker = stickers[i]
    const local = rotate(p.x - sticker.x, p.y - sticker.y, -sticker.rotation)
    if (Math.abs(local.x) <= sticker.width / 2 && Math.abs(local.y) <= sticker.height / 2) return sticker
  }
  return null
}

/** Normalise any angle to −180…180 */
export const normaliseAngle = (degrees: number): number => ((((degrees + 180) % 360) + 360) % 360) - 180

/** Whether the crop mirrors the photo (exactly one flip) */
export const isMirrored = (crop: AdjustCrop) => crop.flipX !== crop.flipY

/**
 * The rotation and flip that make a sticker read upright in the SAVED photo, whatever the crop's rotation and flips
 * (the sticker lives in the photo's own coordinates, which the crop turns and mirrors).
 */
export const uprightStickerPose = (crop: AdjustCrop): { rotation: number, flipX: boolean } => {
  const degrees = totalRotation(crop)
  if (isMirrored(crop)) return { rotation: normaliseAngle(degrees + (crop.flipY ? 180 : 0)), flipX: true }
  return { rotation: normaliseAngle(-degrees - (crop.flipX && crop.flipY ? 180 : 0)), flipX: false }
}

/** A sticker's rotation as the viewer sees it in the saved photo (the Rotate slider), and back */
export const apparentStickerRotation = (sticker: AdjustSticker, crop: AdjustCrop) => {
  const delta = normaliseAngle(sticker.rotation - uprightStickerPose(crop).rotation)
  return isMirrored(crop) ? normaliseAngle(-delta) : delta
}
export const stickerRotationFromApparent = (apparent: number, crop: AdjustCrop) =>
  normaliseAngle(uprightStickerPose(crop).rotation + (isMirrored(crop) ? -apparent : apparent))
